import { useState, useEffect, useCallback } from "react";
import { Trophy, Save, Loader2, Plus, Calendar } from "lucide-react";
import { API, axios } from "@/api";
import { toast } from "sonner";

export default function MonthlyChallengePanel() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const [form, setForm] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    title: "",
    description: "",
    tracking_event: "freebie_claimed",
    tracking_category: "",
    target: 3,
    reward_points: 50,
    reward_badge: "",
    icon: "trophy",
  });

  const fetchChallenges = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/monthly-challenges`);
      setChallenges(r.data.challenges || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const handleSave = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/monthly-challenge`, form);
      toast.success("Challenge saved!");
      fetchChallenges();
    } catch (e) {
      toast.error("Failed to save: " + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const events = [
    { value: "freebie_claimed", label: "Freebie Claimed" },
    { value: "offer_verified", label: "Offer Verified" },
    { value: "itinerary_built", label: "Itinerary Built" },
    { value: "new_city_explored", label: "City Explored" },
    { value: "review_submitted", label: "Review Submitted" },
  ];

  const categories = [
    "", "Auto", "Bakery", "Beauty & Skincare", "Cafes & Drinks",
    "Clothing & Apparel", "Dessert & Ice Cream", "Entertainment", "Fast Food",
    "Grocery", "Health & Fitness", "Home & Garden", "Jewelry & Accessories",
    "Other", "Pet", "Restaurant", "Retail",
  ];

  return (
    <div className="space-y-4" data-testid="monthly-challenge-admin">
      {/* Create / Edit form */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Set Monthly Challenge
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-semibold text-slate-400 uppercase">Year</label>
            <input type="number" value={form.year} onChange={e => setForm(f => ({ ...f, year: +e.target.value }))} className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-400 uppercase">Month</label>
            <select value={form.month} onChange={e => setForm(f => ({ ...f, month: +e.target.value }))} className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm">
              {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase">Challenge Title</label>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="March Dessert Dash" className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
        </div>

        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase">Description</label>
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Claim 3 dessert deals this month" className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-semibold text-slate-400 uppercase">Tracking Event</label>
            <select value={form.tracking_event} onChange={e => setForm(f => ({ ...f, tracking_event: e.target.value }))} className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm">
              {events.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-400 uppercase">Category (optional)</label>
            <select value={form.tracking_category} onChange={e => setForm(f => ({ ...f, tracking_category: e.target.value }))} className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm">
              {categories.map(c => <option key={c} value={c}>{c || "All Categories"}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-semibold text-slate-400 uppercase">Target</label>
            <input type="number" value={form.target} onChange={e => setForm(f => ({ ...f, target: +e.target.value }))} className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-400 uppercase">Points</label>
            <input type="number" value={form.reward_points} onChange={e => setForm(f => ({ ...f, reward_points: +e.target.value }))} className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-400 uppercase">Badge ID</label>
            <input value={form.reward_badge || ""} onChange={e => setForm(f => ({ ...f, reward_badge: e.target.value }))} placeholder="optional" className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} className="w-full py-2 rounded-xl bg-indigo-500 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-indigo-600 transition-colors disabled:opacity-50" data-testid="save-challenge-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Challenge"}
        </button>
      </div>

      {/* Existing challenges */}
      <div className="space-y-2">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Past & Current Challenges
        </h3>
        {loading ? (
          <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-300 mx-auto" /></div>
        ) : challenges.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No challenges set yet</p>
        ) : (
          challenges.map((c, i) => (
            <div key={i} className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-700">{c.title}</p>
                <p className="text-[11px] text-slate-400">{monthNames[(c.month || 1) - 1]} {c.year} · Target: {c.target} · {c.reward_points} pts</p>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                {c.active ? "Active" : "Past"}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
